/**
 * Runtime-neutral Effect program for page-oriented document parsing.
 *
 * The program knows nothing about Workers, Node, R2, Postgres, filesystems, or
 * provider SDKs. Deployment targets select those capabilities by providing the
 * five services below as a Layer.
 */
import { Context, Data, DateTime, Effect, Layer, Schedule, Schema } from 'effect';

import { CanonicalBlockSchema, type CanonicalBlock } from './types';

const positiveInt = Schema.Int.check(Schema.isGreaterThan(0));

export const ParseProviderKindSchema = Schema.Literals(['local', 'self-hosted', 'byok']);
export type ParseProviderKind = typeof ParseProviderKindSchema.Type;

export const ParseDescriptorSchema = Schema.Struct({
  parserId: Schema.String,
  providerId: Schema.String,
  kind: ParseProviderKindSchema,
});
export type ParseDescriptor = typeof ParseDescriptorSchema.Type;

export const ParseRunRequestSchema = Schema.Struct({
  runId: Schema.String,
  sourceId: Schema.String,
  parserId: Schema.String,
  maxAttempts: Schema.optionalKey(positiveInt),
  pageTimeoutMs: Schema.optionalKey(positiveInt),
  concurrency: Schema.optionalKey(positiveInt),
});
export type ParseRunRequest = typeof ParseRunRequestSchema.Type;

export const ParsePageRecordSchema = Schema.Struct({
  page: positiveInt,
  blocks: Schema.Array(CanonicalBlockSchema),
});
export type ParsePageRecord = typeof ParsePageRecordSchema.Type;

export const ParseCheckpointSchema = Schema.Struct({
  completedPages: Schema.Array(positiveInt),
  failedPages: Schema.Array(positiveInt),
});
export type ParseCheckpoint = typeof ParseCheckpointSchema.Type;

export const ParseRunStatusSchema = Schema.Literals([
  'completed',
  'completed_with_errors',
  'cancelled',
]);
export type ParseRunStatus = typeof ParseRunStatusSchema.Type;

export const ParseRunResultSchema = Schema.Struct({
  runId: Schema.String,
  parserId: Schema.String,
  providerId: Schema.String,
  pagesTotal: positiveInt,
  pagesDone: Schema.Int,
  pagesFailed: Schema.Int,
  status: ParseRunStatusSchema,
  startedAt: Schema.Int,
  completedAt: Schema.Int,
});
export type ParseRunResult = typeof ParseRunResultSchema.Type;

export type ParseRunEvent =
  | {
      readonly type: 'started';
      readonly runId: string;
      readonly parserId: string;
      readonly providerId: string;
      readonly pagesTotal: number;
      readonly at: number;
    }
  | {
      readonly type: 'page_completed';
      readonly runId: string;
      readonly page: number;
      readonly at: number;
    }
  | {
      readonly type: 'page_failed';
      readonly runId: string;
      readonly page: number;
      readonly code: string;
      readonly at: number;
    }
  | {
      readonly type: 'completed' | 'cancelled';
      readonly runId: string;
      readonly status: ParseRunStatus;
      readonly pagesDone: number;
      readonly pagesFailed: number;
      readonly at: number;
    };

export type ParseRuntimePort = 'catalog' | 'source' | 'provider' | 'checkpoint' | 'events';

/** A redacted port failure. Provider credentials and source bytes never enter this shape. */
export class ParseRuntimePortError extends Data.TaggedError('ParseRuntimePortError')<{
  readonly port: ParseRuntimePort;
  readonly operation: string;
  readonly code: string;
  readonly retryable: boolean;
  readonly parserId?: string;
  readonly page?: number;
}> {}

export class ParseRunRequestError extends Data.TaggedError('ParseRunRequestError')<{
  readonly code: 'invalid_parse_run_request';
}> {}

export class ParseRunHasNoPages extends Data.TaggedError('ParseRunHasNoPages')<{
  readonly parserId: string;
}> {}

export type ParseRunError = ParseRuntimePortError | ParseRunRequestError | ParseRunHasNoPages;

const CatalogTypeId = 'okrapdf/parser-sdk/ParserCatalog/TypeId' as const;
const SourceTypeId = 'okrapdf/parser-sdk/ParseSource/TypeId' as const;
const ProviderTypeId = 'okrapdf/parser-sdk/ParseProvider/TypeId' as const;
const CheckpointTypeId = 'okrapdf/parser-sdk/ParseCheckpointStore/TypeId' as const;
const EventsTypeId = 'okrapdf/parser-sdk/ParseEventSink/TypeId' as const;

export interface ParserCatalogService {
  readonly [CatalogTypeId]: typeof CatalogTypeId;
  readonly resolve: (parserId: string) => Effect.Effect<ParseDescriptor, ParseRuntimePortError>;
}

export const ParserCatalog: Context.Service<ParserCatalogService, ParserCatalogService> =
  Context.Service('okrapdf/parser-sdk/ParserCatalog');

export interface ParseSourceService {
  readonly [SourceTypeId]: typeof SourceTypeId;
  readonly load: (sourceId: string) => Effect.Effect<Uint8Array, ParseRuntimePortError>;
}

export const ParseSource: Context.Service<ParseSourceService, ParseSourceService> = Context.Service(
  'okrapdf/parser-sdk/ParseSource',
);

export interface ParseProviderService {
  readonly [ProviderTypeId]: typeof ProviderTypeId;
  readonly countPages: (
    descriptor: ParseDescriptor,
    source: Uint8Array,
  ) => Effect.Effect<number, ParseRuntimePortError>;
  readonly parsePage: (
    descriptor: ParseDescriptor,
    source: Uint8Array,
    page: number,
  ) => Effect.Effect<ReadonlyArray<CanonicalBlock>, ParseRuntimePortError>;
}

export const ParseProvider: Context.Service<ParseProviderService, ParseProviderService> =
  Context.Service('okrapdf/parser-sdk/ParseProvider');

export interface ParseCheckpointStoreService {
  readonly [CheckpointTypeId]: typeof CheckpointTypeId;
  readonly load: (runId: string) => Effect.Effect<ParseCheckpoint, ParseRuntimePortError>;
  readonly isActive: (runId: string) => Effect.Effect<boolean, ParseRuntimePortError>;
  readonly recordPage: (
    runId: string,
    record: ParsePageRecord | { readonly page: number; readonly errorCode: string },
  ) => Effect.Effect<void, ParseRuntimePortError>;
  readonly finish: (
    runId: string,
    status: ParseRunStatus,
  ) => Effect.Effect<void, ParseRuntimePortError>;
}

export const ParseCheckpointStore: Context.Service<
  ParseCheckpointStoreService,
  ParseCheckpointStoreService
> = Context.Service('okrapdf/parser-sdk/ParseCheckpointStore');

export interface ParseEventSinkService {
  readonly [EventsTypeId]: typeof EventsTypeId;
  readonly emit: (event: ParseRunEvent) => Effect.Effect<void, ParseRuntimePortError>;
}

export const ParseEventSink: Context.Service<ParseEventSinkService, ParseEventSinkService> =
  Context.Service('okrapdf/parser-sdk/ParseEventSink');

export type ParseRuntimeServices =
  | ParserCatalogService
  | ParseSourceService
  | ParseProviderService
  | ParseCheckpointStoreService
  | ParseEventSinkService;

export interface ParseRuntimeLayerOptions {
  readonly catalog: ParserCatalogService;
  readonly source: ParseSourceService;
  readonly provider: ParseProviderService;
  readonly checkpoint: ParseCheckpointStoreService;
  readonly events: ParseEventSinkService;
}

export function makeParserCatalogService(
  resolve: ParserCatalogService['resolve'],
): ParserCatalogService {
  return ParserCatalog.of({ [CatalogTypeId]: CatalogTypeId, resolve });
}

export function makeParseSourceService(load: ParseSourceService['load']): ParseSourceService {
  return ParseSource.of({ [SourceTypeId]: SourceTypeId, load });
}

export function makeParseProviderService(options: {
  readonly countPages: ParseProviderService['countPages'];
  readonly parsePage: ParseProviderService['parsePage'];
}): ParseProviderService {
  return ParseProvider.of({ [ProviderTypeId]: ProviderTypeId, ...options });
}

export function makeParseCheckpointStoreService(
  options: Omit<ParseCheckpointStoreService, typeof CheckpointTypeId>,
): ParseCheckpointStoreService {
  return ParseCheckpointStore.of({ [CheckpointTypeId]: CheckpointTypeId, ...options });
}

export function makeParseEventSinkService(
  emit: ParseEventSinkService['emit'],
): ParseEventSinkService {
  return ParseEventSink.of({ [EventsTypeId]: EventsTypeId, emit });
}

/** Symmetric composition primitive used by memory, Node, and Cloudflare targets. */
export function parseRuntimeLayer(
  options: ParseRuntimeLayerOptions,
): Layer.Layer<ParseRuntimeServices> {
  return Layer.mergeAll(
    Layer.succeed(ParserCatalog, options.catalog),
    Layer.succeed(ParseSource, options.source),
    Layer.succeed(ParseProvider, options.provider),
    Layer.succeed(ParseCheckpointStore, options.checkpoint),
    Layer.succeed(ParseEventSink, options.events),
  );
}

function nowMillis(): Effect.Effect<number> {
  return DateTime.now.pipe(Effect.map(DateTime.toEpochMillis));
}

function retryProvider<A>(
  program: () => Effect.Effect<A, ParseRuntimePortError>,
  options: { readonly attempts: number; readonly timeoutMs?: number },
): Effect.Effect<A, ParseRuntimePortError> {
  const attempt = Effect.suspend(program);
  const timed = options.timeoutMs
    ? attempt.pipe(
        Effect.timeoutOrElse({
          duration: options.timeoutMs,
          orElse: () =>
            new ParseRuntimePortError({
              port: 'provider',
              operation: 'timeout',
              code: 'provider_timeout',
              retryable: true,
            }),
        }),
      )
    : attempt;
  return timed.pipe(
    Effect.retry({
      while: (error) => error.retryable,
      schedule: Schedule.recurs(options.attempts - 1),
    }),
  );
}

const decodeRequest = (input: unknown) =>
  Schema.decodeUnknownEffect(ParseRunRequestSchema)(input).pipe(
    Effect.mapError(() => new ParseRunRequestError({ code: 'invalid_parse_run_request' })),
  );

/**
 * The single parse lifecycle. All deployment-specific behavior arrives through
 * Layers; changing target or provider never changes this control flow.
 */
export const runParseProgramEffect = Effect.fn('okrapdf/parser-sdk.runParseProgram')(function* (
  input: unknown,
) {
  const request = yield* decodeRequest(input);
  const catalog = yield* ParserCatalog;
  const sourcePort = yield* ParseSource;
  const provider = yield* ParseProvider;
  const checkpoints = yield* ParseCheckpointStore;
  const events = yield* ParseEventSink;
  const descriptor = yield* catalog.resolve(request.parserId);
  const source = yield* sourcePort.load(request.sourceId);
  const attempts = request.maxAttempts ?? 3;
  const timeoutMs = request.pageTimeoutMs;
  const startedAt = yield* nowMillis();
  const pagesTotal = yield* retryProvider(() => provider.countPages(descriptor, source), {
    attempts,
    timeoutMs,
  });
  if (pagesTotal < 1 || !Number.isInteger(pagesTotal)) {
    return yield* new ParseRunHasNoPages({ parserId: descriptor.parserId });
  }
  const checkpoint = yield* checkpoints.load(request.runId);
  const settled = new Set([...checkpoint.completedPages, ...checkpoint.failedPages]);
  yield* events.emit({
    type: 'started',
    runId: request.runId,
    parserId: descriptor.parserId,
    providerId: descriptor.providerId,
    pagesTotal,
    at: startedAt,
  });

  const pages = Array.from({ length: pagesTotal }, (_, index) => index + 1).filter(
    (page) => !settled.has(page),
  );
  const outcomes = yield* Effect.forEach(
    pages,
    (page) =>
      Effect.gen(function* () {
        const active = yield* checkpoints.isActive(request.runId);
        if (!active) return { page, status: 'cancelled' as const };
        const outcome = yield* retryProvider(() => provider.parsePage(descriptor, source, page), {
          attempts,
          timeoutMs,
        }).pipe(
          Effect.flatMap((blocks) =>
            Schema.decodeUnknownEffect(Schema.Array(CanonicalBlockSchema))(blocks).pipe(
              Effect.mapError(
                () =>
                  new ParseRuntimePortError({
                    port: 'provider',
                    operation: 'normalize_page',
                    code: 'invalid_provider_output',
                    retryable: false,
                    parserId: descriptor.parserId,
                    page,
                  }),
              ),
            ),
          ),
          Effect.matchEffect({
            onFailure: (error) =>
              Effect.gen(function* () {
                yield* checkpoints.recordPage(request.runId, { page, errorCode: error.code });
                const at = yield* nowMillis();
                yield* events.emit({
                  type: 'page_failed',
                  runId: request.runId,
                  page,
                  code: error.code,
                  at,
                });
                return { page, status: 'failed' as const };
              }),
            onSuccess: (blocks) =>
              Effect.gen(function* () {
                yield* checkpoints.recordPage(request.runId, { page, blocks });
                const at = yield* nowMillis();
                yield* events.emit({
                  type: 'page_completed',
                  runId: request.runId,
                  page,
                  at,
                });
                return { page, status: 'completed' as const };
              }),
          }),
        );
        return outcome;
      }),
    { concurrency: request.concurrency ?? 1 },
  );

  const cancelled = outcomes.some((outcome) => outcome.status === 'cancelled');
  const finalCheckpoint = yield* checkpoints.load(request.runId);
  const pagesDone = finalCheckpoint.completedPages.length;
  const pagesFailed = finalCheckpoint.failedPages.length;
  const status: ParseRunStatus = cancelled
    ? 'cancelled'
    : pagesFailed > 0
      ? 'completed_with_errors'
      : 'completed';
  yield* checkpoints.finish(request.runId, status);
  const completedAt = yield* nowMillis();
  yield* events.emit({
    type: cancelled ? 'cancelled' : 'completed',
    runId: request.runId,
    status,
    pagesDone,
    pagesFailed,
    at: completedAt,
  });
  return {
    runId: request.runId,
    parserId: descriptor.parserId,
    providerId: descriptor.providerId,
    pagesTotal,
    pagesDone,
    pagesFailed,
    status,
    startedAt,
    completedAt,
  } satisfies ParseRunResult;
});

export interface MemoryParseRuntime {
  readonly layer: Layer.Layer<ParseRuntimeServices>;
  readonly checkpoints: Map<string, ParseCheckpoint & { status?: ParseRunStatus }>;
  readonly events: Array<ParseRunEvent>;
}

/** In-memory target pack used by conformance tests and embedders. */
export function makeMemoryParseRuntime(options: {
  readonly descriptors: ReadonlyArray<ParseDescriptor>;
  readonly sources: ReadonlyMap<string, Uint8Array>;
  readonly provider: ParseProviderService;
}): MemoryParseRuntime {
  const descriptors = new Map(options.descriptors.map((row) => [row.parserId, row]));
  const checkpoints = new Map<string, ParseCheckpoint & { status?: ParseRunStatus }>();
  const events: Array<ParseRunEvent> = [];
  const catalog = makeParserCatalogService((parserId) => {
    const descriptor = descriptors.get(parserId);
    return descriptor
      ? Effect.succeed(descriptor)
      : Effect.fail(
          new ParseRuntimePortError({
            port: 'catalog',
            operation: 'resolve',
            code: 'parser_not_found',
            retryable: false,
            parserId,
          }),
        );
  });
  const source = makeParseSourceService((sourceId) => {
    const bytes = options.sources.get(sourceId);
    return bytes
      ? Effect.succeed(bytes)
      : Effect.fail(
          new ParseRuntimePortError({
            port: 'source',
            operation: 'load',
            code: 'source_not_found',
            retryable: false,
          }),
        );
  });
  const checkpoint = makeParseCheckpointStoreService({
    load: (runId) =>
      Effect.sync(() => {
        const value = checkpoints.get(runId);
        return {
          completedPages: [...(value?.completedPages ?? [])],
          failedPages: [...(value?.failedPages ?? [])],
        };
      }),
    isActive: () => Effect.succeed(true),
    recordPage: (runId, record) =>
      Effect.sync(() => {
        const current = checkpoints.get(runId) ?? { completedPages: [], failedPages: [] };
        const completedPages = [...current.completedPages];
        const failedPages = [...current.failedPages];
        if ('blocks' in record) completedPages.push(record.page);
        else failedPages.push(record.page);
        checkpoints.set(runId, {
          completedPages: [...new Set(completedPages)].sort((a, b) => a - b),
          failedPages: [...new Set(failedPages)].sort((a, b) => a - b),
          ...(current.status ? { status: current.status } : {}),
        });
      }),
    finish: (runId, status) =>
      Effect.sync(() => {
        const current = checkpoints.get(runId) ?? { completedPages: [], failedPages: [] };
        checkpoints.set(runId, { ...current, status });
      }),
  });
  const eventSink = makeParseEventSinkService((event) =>
    Effect.sync(() => {
      events.push(event);
    }),
  );
  return {
    layer: parseRuntimeLayer({
      catalog,
      source,
      provider: options.provider,
      checkpoint,
      events: eventSink,
    }),
    checkpoints,
    events,
  };
}

/** Shared, framework-free contract suite for every deployment Layer pack. */
export async function parseRuntimeContractSuite<E>(
  name: string,
  layer: Layer.Layer<ParseRuntimeServices, E>,
  request: ParseRunRequest,
  expected: {
    readonly parserId: string;
    readonly providerId: string;
    readonly pagesTotal: number;
    readonly status?: ParseRunStatus;
  },
): Promise<ParseRunResult> {
  const result = await Effect.runPromise(
    runParseProgramEffect(request).pipe(Effect.provide(layer)),
  );
  if (result.parserId !== expected.parserId) {
    throw new Error(`${name}: parserId must be ${expected.parserId}`);
  }
  if (result.providerId !== expected.providerId) {
    throw new Error(`${name}: providerId must be ${expected.providerId}`);
  }
  if (result.pagesTotal !== expected.pagesTotal) {
    throw new Error(`${name}: pagesTotal must be ${expected.pagesTotal}`);
  }
  if (result.pagesDone + result.pagesFailed !== result.pagesTotal) {
    throw new Error(`${name}: every page must reach one terminal checkpoint`);
  }
  if (expected.status && result.status !== expected.status) {
    throw new Error(`${name}: status must be ${expected.status}`);
  }
  return result;
}
